import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  useTheme,
  alpha,
  Tooltip,
  Popper,
  ClickAwayListener,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatListBulleted as BulletIcon,
  FormatListNumbered as NumberedIcon,
  Code as CodeIcon,
  Link as LinkIcon,
  TableChart as TableIcon,
  HorizontalRule as HRuleIcon,
  FormatQuote as QuoteIcon,
  Title as HeaderIcon,
  ViewColumn as ColumnsIcon,
  CheckBox as ChecklistIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as DangerIcon,
  CheckCircle as SuccessIcon,
  List as TOCIcon,
  Visibility as PreviewIcon,
  VisibilityOff as EditIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MarkdownToolbar = ({ onInsert }) => {
  const theme = useTheme();

  const tools = [
    { icon: <BoldIcon />, action: () => onInsert('**', '**'), tooltip: 'Bold (Ctrl+B)', shortcut: 'Ctrl+B' },
    { icon: <ItalicIcon />, action: () => onInsert('*', '*'), tooltip: 'Italic (Ctrl+I)', shortcut: 'Ctrl+I' },
    { icon: <BulletIcon />, action: () => onInsert('\n- ', ''), tooltip: 'Bullet List (Ctrl+Shift+8)', shortcut: 'Ctrl+Shift+8' },
    { icon: <NumberedIcon />, action: () => onInsert('\n1. ', ''), tooltip: 'Numbered List (Ctrl+Shift+7)', shortcut: 'Ctrl+Shift+7' },
    { icon: <CodeIcon />, action: () => onInsert('`', '`'), tooltip: 'Code (Ctrl+`)', shortcut: 'Ctrl+`' },
    { icon: <LinkIcon />, action: () => onInsert('[', '](url)'), tooltip: 'Link (Ctrl+K)', shortcut: 'Ctrl+K' },
    { icon: <TableIcon />, action: () => onInsert('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', ''), tooltip: 'Table (Ctrl+T)', shortcut: 'Ctrl+T' },
    { icon: <HRuleIcon />, action: () => onInsert('\n---\n', ''), tooltip: 'Horizontal Rule (Ctrl+H)', shortcut: 'Ctrl+H' }
  ];

  return (
    <Box sx={{ 
      display: 'flex', 
      gap: 1, 
      p: 2, 
      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
      background: alpha(theme.palette.grey[50], 0.8)
    }}>
      {tools.map((tool, index) => (
        <Tooltip
          key={index}
          title={tool.tooltip}
          placement="top"
          arrow
        >
          <IconButton
            size="small"
            onClick={tool.action}
            sx={{
              border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              borderRadius: 2,
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: theme.palette.primary.main
              },
              '&:active': {
                transform: 'scale(0.95)'
              },
              transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.1s ease'
            }}
          >
            {tool.icon}
          </IconButton>
        </Tooltip>
      ))}
    </Box>
  );
};

const JobDescriptionMarkdown = ({ value, onChange, onBlur, disabled = false, placeholder = "" }) => {
  const theme = useTheme();
  const [descriptionRef, setDescriptionRef] = useState(null);
  const [previewMode, setPreviewMode] = useState(true);
  const [commandMenuOpen, setCommandMenuOpen] = useState(false);
  const [commandMenuAnchor, setCommandMenuAnchor] = useState(null);
  const [filteredCommands, setFilteredCommands] = useState([]);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [commandJustExecuted, setCommandJustExecuted] = useState(false);

  const commandSections = [
    {
      title: 'FORMAT',
      commands: [
        { name: '/heading1', alias: '/h1', icon: <HeaderIcon />, description: 'Big section heading', action: () => insertMarkdown('\n# ', '') },
        { name: '/heading2', alias: '/h2', icon: <HeaderIcon />, description: 'Medium section heading', action: () => insertMarkdown('\n## ', '') },
        { name: '/heading3', alias: '/h3', icon: <HeaderIcon />, description: 'Small section heading', action: () => insertMarkdown('\n### ', '') },
        { name: '/text', alias: '/p', icon: <AssignmentIcon />, description: 'Paragraph block', action: () => insertMarkdown('\n', '') },
        { name: '/quote', alias: '/q', icon: <QuoteIcon />, description: 'Add a blockquote section', action: () => insertMarkdown('\n> ', '') },
        { name: '/code', alias: '/c', icon: <CodeIcon />, description: 'Add a code section', action: () => insertMarkdown('\n```\n', '\n```\n') },
        { name: '/bold', alias: '/b', icon: <BoldIcon />, description: 'Bold text', action: () => insertMarkdown('**', '**') },
        { name: '/italic', alias: '/i', icon: <ItalicIcon />, description: 'Italic text', action: () => insertMarkdown('*', '*') }
      ]
    },
    {
      title: 'STRUCTURE',
      commands: [
        { name: '/separator', alias: '/hr', icon: <HRuleIcon />, description: 'Insert a horizontal rule separator', action: () => insertMarkdown('\n---\n', '') },
        { name: '/bullet', alias: '/ul', icon: <BulletIcon />, description: 'Create a simple bulleted list', action: () => insertMarkdown('\n- ', '') },
        { name: '/number', alias: '/ol', icon: <NumberedIcon />, description: 'Create a list with numbering', action: () => insertMarkdown('\n1. ', '') },
        { name: '/checklist', alias: '/check', icon: <ChecklistIcon />, description: 'Track tasks with a checklist', action: () => insertMarkdown('\n- [ ] ', '') },
        { name: '/2columns', alias: '/2col', icon: <ColumnsIcon />, description: 'Convert into 2 columns', action: () => insertMarkdown('\n| Column 1 | Column 2 |\n|----------|----------|\n| ', ' | |\n') },
        { name: '/3columns', alias: '/3col', icon: <ColumnsIcon />, description: 'Convert into 3 columns', action: () => insertMarkdown('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| ', ' | | |\n') },
        { name: '/table', alias: '/t', icon: <TableIcon />, description: 'Insert a table', action: () => insertMarkdown('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', '') },
        { name: '/toc', alias: '/contents', icon: <TOCIcon />, description: 'Table of Contents', action: () => generateTableOfContents() }
      ]
    },
    {
      title: 'BANNER',
      commands: [
        { name: '/info', alias: '/i', icon: <InfoIcon />, description: 'Insert an info banner', action: () => insertMarkdown('\n> ℹ️ **Info:** ', '\n') },
        { name: '/success', alias: '/s', icon: <SuccessIcon />, description: 'Insert a success banner', action: () => insertMarkdown('\n> ✅ **Success:** ', '\n') },
        { name: '/warning', alias: '/w', icon: <WarningIcon />, description: 'Insert a warning banner', action: () => insertMarkdown('\n> ⚠️ **Warning:** ', '\n') },
        { name: '/danger', alias: '/d', icon: <DangerIcon />, description: 'Insert a danger banner', action: () => insertMarkdown('\n> ❌ **Danger:** ', '\n') }
      ]
    },
    {
      title: 'NAVIGATION',
      commands: [
        { name: '/link', alias: '/l', icon: <LinkIcon />, description: 'Add a link', action: () => insertMarkdown('[', '](url)') }
      ]
    }
  ];

  // Flatten commands for filtering
  const commands = commandSections.flatMap(section => section.commands);

  const handleInputChange = (newValue) => {
    onChange(newValue);
    
    // Handle slash command menu
    const cursorPos = descriptionRef?.selectionStart || newValue.length;
    const beforeCursor = newValue.substring(0, cursorPos);
    const lines = beforeCursor.split('\n');
    const currentLine = lines[lines.length - 1];
    
    // Show menu when line starts with / (even just "/")
    if (currentLine.startsWith('/')) {
      const searchTerm = currentLine.substring(1).toLowerCase(); // Remove the /
      let filtered;
      
      if (searchTerm === '') {
        // Show all commands when just "/" is typed
        filtered = commands;
      } else {
        // Filter commands based on search term - more flexible matching
        filtered = commands.filter(cmd => {
          const nameMatch = cmd.name.substring(1).toLowerCase().includes(searchTerm);
          const aliasMatch = cmd.alias.substring(1).toLowerCase().includes(searchTerm);
          const descMatch = cmd.description.toLowerCase().includes(searchTerm);
          
          // Special keyword matching
          const keywordMatches = {
            'head': cmd.name.includes('heading'),
            'header': cmd.name.includes('heading'),
            'h': cmd.name.includes('heading'),
            'list': cmd.name.includes('bullet') || cmd.name.includes('number') || cmd.name.includes('checklist'),
            'table': cmd.name.includes('table') || cmd.name.includes('column'),
            'banner': cmd.name.includes('info') || cmd.name.includes('success') || cmd.name.includes('warning') || cmd.name.includes('danger'),
            'format': cmd.name.includes('bold') || cmd.name.includes('italic') || cmd.name.includes('code'),
            'text': cmd.name.includes('text') || cmd.name.includes('quote') || cmd.name.includes('bold') || cmd.name.includes('italic')
          };
          
          const keywordMatch = keywordMatches[searchTerm] || false;
          
          return nameMatch || aliasMatch || descMatch || keywordMatch;
        });
      }
      
      if (filtered.length > 0) {
        setFilteredCommands(filtered);
        setSelectedCommandIndex(0);
        setCommandMenuOpen(true);
        
        // Position menu at cursor line - simplified
        setTimeout(() => {
          if (descriptionRef) {
            const textarea = descriptionRef;
            const rect = textarea.getBoundingClientRect();
            
            setCommandMenuAnchor({
              getBoundingClientRect: () => ({
                left: rect.left + 10,
                top: rect.top + 40,
                right: rect.left + 10,
                bottom: rect.top + 40,
                width: 0,
                height: 0
              })
            });
          }
        }, 10);
      } else {
        setCommandMenuOpen(false);
      }
    } else {
      setCommandMenuOpen(false);
    }
  };

  const executeCommand = (command) => {
    const textarea = descriptionRef;
    const currentValue = value;
    const cursorPos = textarea?.selectionStart || currentValue.length;
    const beforeCursor = currentValue.substring(0, cursorPos);
    const afterCursor = currentValue.substring(cursorPos);
    
    // Find the start of the current line
    const lineStart = beforeCursor.lastIndexOf('\n') + 1;
    const currentLine = beforeCursor.substring(lineStart);
    
    // Remove the entire slash command (including the /) from current line
    const slashIndex = currentLine.lastIndexOf('/');
    if (slashIndex !== -1) {
      const beforeSlash = beforeCursor.substring(0, lineStart + slashIndex);
      const cleanedText = beforeSlash + afterCursor;
      const newCursorPos = lineStart + slashIndex;
      
      setCommandMenuOpen(false);
      setCommandJustExecuted(true);
      
      // Execute command directly with cleaned text
      setTimeout(() => {
        // Manually execute the markdown insertion with cleaned text
        const insertAt = newCursorPos;
        let finalText;
        
        if (command.name === '/bold') {
          finalText = cleanedText.substring(0, insertAt) + '****' + cleanedText.substring(insertAt);
          onChange(finalText);
          setTimeout(() => {
            textarea?.setSelectionRange(insertAt + 2, insertAt + 2);
          }, 0);
        } else if (command.name === '/italic') {
          finalText = cleanedText.substring(0, insertAt) + '**' + cleanedText.substring(insertAt);
          onChange(finalText);
          setTimeout(() => {
            textarea?.setSelectionRange(insertAt + 1, insertAt + 1);
          }, 0);
        } else {
          // For other commands, extract the before/after parts from the action
          const commandActions = {
            '/heading1': { before: '\n# ', after: '' },
            '/heading2': { before: '\n## ', after: '' },
            '/heading3': { before: '\n### ', after: '' },
            '/text': { before: '\n', after: '' },
            '/quote': { before: '\n> ', after: '' },
            '/code': { before: '\n```\n', after: '\n```\n' },
            '/separator': { before: '\n---\n', after: '' },
            '/bullet': { before: '\n- ', after: '' },
            '/number': { before: '\n1. ', after: '' },
            '/checklist': { before: '\n- [ ] ', after: '' },
            '/2columns': { before: '\n| Column 1 | Column 2 |\n|----------|----------|\n| ', after: ' | |\n' },
            '/3columns': { before: '\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| ', after: ' | | |\n' },
            '/table': { before: '\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', after: '' },
            '/info': { before: '\n> ℹ️ **Info:** ', after: '\n' },
            '/success': { before: '\n> ✅ **Success:** ', after: '\n' },
            '/warning': { before: '\n> ⚠️ **Warning:** ', after: '\n' },
            '/danger': { before: '\n> ❌ **Danger:** ', after: '\n' },
            '/link': { before: '[', after: '](url)' },
            '/toc': null // Special handling for TOC
          };
          
          const action = commandActions[command.name];
          if (action === null && command.name === '/toc') {
            // Special handling for Table of Contents - use the cleaned text
            setTimeout(() => {
              // Set the cleaned text first (without /toc)
              onChange(cleanedText);
              
              // Then generate TOC with the cleaned text
              setTimeout(() => {
                if (textarea) {
                  textarea.focus();
                  textarea.setSelectionRange(insertAt, insertAt);
                }
                
                // Generate TOC using the cleaned text
                const currentText = cleanedText;
                const lines = currentText.split('\n');
                const headers = [];
                
                // Find all headers in the document
                lines.forEach((line, index) => {
                  const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
                  if (headerMatch) {
                    const level = headerMatch[1].length;
                    const title = headerMatch[2].trim();
                    const anchor = title.toLowerCase()
                      .replace(/[^\w\s-]/g, '')
                      .replace(/\s+/g, '-')
                      .replace(/-+/g, '-')
                      .replace(/^-|-$/g, '');
                    
                    headers.push({ level, title, anchor });
                  }
                });
                
                // Generate TOC markdown
                let tocMarkdown;
                
                if (headers.length === 0) {
                  tocMarkdown = `## Table of Contents

> Add headings to fill the Table of Contents

*Use # for main sections, ## for subsections, etc.*`;
                } else {
                  tocMarkdown = '## Table of Contents\n\n';
                  headers.forEach(header => {
                    const indent = '  '.repeat(Math.max(0, header.level - 1));
                    tocMarkdown += `${indent}- [${header.title}](#${header.anchor})\n`;
                  });
                }
                
                // Insert TOC at cursor position
                const beforeCursor = currentText.substring(0, insertAt);
                const afterCursor = currentText.substring(insertAt);
                const newText = beforeCursor + '\n' + tocMarkdown + '\n' + afterCursor;
                
                onChange(newText);
                
                // Position cursor after the TOC
                setTimeout(() => {
                  const newCursorPos = insertAt + tocMarkdown.length + 2;
                  textarea?.setSelectionRange(newCursorPos, newCursorPos);
                }, 0);
              }, 10);
            }, 0);
          } else if (action) {
            finalText = cleanedText.substring(0, insertAt) + action.before + action.after + cleanedText.substring(insertAt);
            onChange(finalText);
            setTimeout(() => {
              textarea?.setSelectionRange(insertAt + action.before.length, insertAt + action.before.length);
            }, 0);
          }
        }
        
        // Reset the flag after command execution
        setTimeout(() => {
          setCommandJustExecuted(false);
        }, 200);
      }, 0);
    }
  };

  const handleKeyDown = (e) => {
    // Handle command menu navigation
    if (commandMenuOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCommandIndex(prev => 
          prev < filteredCommands.length - 1 ? prev + 1 : 0
        );
        return;
      }
      
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCommandIndex(prev => 
          prev > 0 ? prev - 1 : filteredCommands.length - 1
        );
        return;
      }
      
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (filteredCommands[selectedCommandIndex]) {
          executeCommand(filteredCommands[selectedCommandIndex]);
        }
        return;
      }
      
      if (e.key === 'Escape') {
        e.preventDefault();
        setCommandMenuOpen(false);
        return;
      }
    }

    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          insertMarkdown('**', '**');
          return;
        case 'i':
          e.preventDefault();
          insertMarkdown('*', '*');
          return;
        case 'k':
          e.preventDefault();
          insertMarkdown('[', '](url)');
          return;
        case '`':
          e.preventDefault();
          insertMarkdown('`', '`');
          return;
        case 't':
          e.preventDefault();
          insertMarkdown('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', '');
          return;
        case 'h':
          e.preventDefault();
          insertMarkdown('\n---\n', '');
          return;
      }
      
      // Handle Ctrl+Shift combinations
      if (e.shiftKey) {
        switch (e.key) {
          case '*': // Ctrl+Shift+8
            e.preventDefault();
            insertMarkdown('\n- ', '');
            return;
          case '&': // Ctrl+Shift+7
            e.preventDefault();
            insertMarkdown('\n1. ', '');
            return;
        }
      }
    }

    // Handle Enter key for list continuation
    if (e.key === 'Enter' && !e.shiftKey) {
      const textarea = e.target;
      const currentValue = textarea.value;
      const cursorPos = textarea.selectionStart;
      
      // Get the current line
      const lines = currentValue.substring(0, cursorPos).split('\n');
      const currentLine = lines[lines.length - 1];
      
      // Check for bullet points
      const bulletMatch = currentLine.match(/^(\s*)-\s/);
      if (bulletMatch) {
        e.preventDefault();
        const indent = bulletMatch[1];
        const newText = currentValue.substring(0, cursorPos) + '\n' + indent + '- ' + currentValue.substring(cursorPos);
        onChange(newText);
        
        setTimeout(() => {
          const newCursorPos = cursorPos + indent.length + 3;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
        return;
      }
      
      // Check for numbered lists
      const numberMatch = currentLine.match(/^(\s*)(\d+)\.\s/);
      if (numberMatch) {
        e.preventDefault();
        const indent = numberMatch[1];
        const nextNumber = parseInt(numberMatch[2]) + 1;
        const newText = currentValue.substring(0, cursorPos) + '\n' + indent + nextNumber + '. ' + currentValue.substring(cursorPos);
        onChange(newText);
        
        setTimeout(() => {
          const newCursorPos = cursorPos + indent.length + nextNumber.toString().length + 3;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
        return;
      }
    }
  };

  const insertMarkdown = (before, after) => {
    if (descriptionRef) {
      const start = descriptionRef.selectionStart || 0;
      const end = descriptionRef.selectionEnd || 0;
      const selectedText = value.substring(start, end);
      const newText = value.substring(0, start) + 
                     before + selectedText + after + 
                     value.substring(end);
      
      onChange(newText);
      
      setTimeout(() => {
        descriptionRef.focus();
        descriptionRef.setSelectionRange(
          start + before.length,
          start + before.length + selectedText.length
        );
      }, 0);
    } else {
      const newText = value + before + after;
      onChange(newText);
    }
  };

  const generateTableOfContents = () => {
    // Get current text
    const currentText = value;
    const lines = currentText.split('\n');
    const headers = [];
    
    // Find all headers in the document
    lines.forEach((line, index) => {
      const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        const title = headerMatch[2].trim();
        const anchor = title.toLowerCase()
          .replace(/[^\w\s-]/g, '') // Remove special chars
          .replace(/\s+/g, '-') // Replace spaces with hyphens
          .replace(/-+/g, '-') // Replace multiple hyphens with single
          .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
        
        headers.push({ level, title, anchor });
      }
    });
    
    // Generate TOC markdown
    let tocMarkdown;
    
    if (headers.length === 0) {
      // No headers found, insert a sample TOC
      tocMarkdown = `## Table of Contents

> Add headings to fill the Table of Contents

*Use # for main sections, ## for subsections, etc.*`;
    } else {
      // Generate actual TOC
      tocMarkdown = '## Table of Contents\n\n';
      
      headers.forEach(header => {
        const indent = '  '.repeat(Math.max(0, header.level - 1));
        tocMarkdown += `${indent}- [${header.title}](#${header.anchor})\n`;
      });
    }
    
    // Get cursor position and insert the TOC
    const textarea = descriptionRef;
    const cursorPos = textarea?.selectionStart || 0;
    const beforeCursor = currentText.substring(0, cursorPos);
    const afterCursor = currentText.substring(cursorPos);
    
    const newText = beforeCursor + '\n' + tocMarkdown + '\n' + afterCursor;
    onChange(newText);
    
    // Position cursor after the TOC
    setTimeout(() => {
      const newCursorPos = cursorPos + tocMarkdown.length + 2;
      textarea?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <Box sx={{ 
      position: 'relative', 
      height: '800px',
      display: 'flex',
      border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
      borderRadius: 3,
      overflow: 'hidden'
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* Header with Preview Toggle */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2, 
          pb: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem' }}>
            Job Description
          </Typography>
          <IconButton
            onClick={() => setPreviewMode(!previewMode)}
            sx={{
              border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              borderRadius: 2,
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: theme.palette.primary.main
              }
            }}
          >
            {previewMode ? <EditIcon /> : <PreviewIcon />}
          </IconButton>
        </Box>

        {!previewMode && (
          <MarkdownToolbar onInsert={insertMarkdown} />
        )}
        {previewMode && (
          <Box sx={{ height: '73px' }} /> // Reserve space for toolbar when hidden
        )}

        {/* Editor/Preview Area */}
        <Box sx={{ 
          position: 'relative', 
          flex: 1,
          display: 'flex',
          overflow: 'hidden'
        }}>
          {/* Left Side - Markdown Input */}
          <Box sx={{ 
            width: previewMode ? '0%' : (value ? '50%' : '100%'), // Full width when no content
            height: '100%',
            transition: 'width 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
            opacity: previewMode ? 0 : 1
          }}>
            <TextField
              fullWidth
              multiline
              disabled={disabled}
              value={value}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={() => {
                // Don't trigger onBlur if command menu is open or command just executed
                if (!commandMenuOpen && !commandJustExecuted && onBlur) {
                  setTimeout(() => {
                    onBlur();
                  }, 100);
                }
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => setCursorPosition(descriptionRef?.selectionStart || 0)}
              onClick={() => setCursorPosition(descriptionRef?.selectionStart || 0)}
              onKeyUp={() => setCursorPosition(descriptionRef?.selectionStart || 0)}
              placeholder={placeholder}
              inputRef={setDescriptionRef}
              sx={{
                height: '100%',
                '& .MuiInputBase-root': {
                  height: '100%',
                  alignItems: 'stretch',
                  borderRadius: 0
                },
                '& .MuiInputBase-inputMultiline': {
                  height: '100% !important',
                  resize: 'none',
                  overflow: 'auto !important'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                }
              }}
              InputProps={{
                sx: {
                  fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                  height: '100%',
                  padding: 2,
                  borderRadius: 0
                }
              }}
            />
            
            {/* Slash Command Hint Overlay */}
            {(() => {
              if (!descriptionRef || disabled || !document.activeElement || document.activeElement !== descriptionRef) {
                return null;
              }
              
              const cursorPos = cursorPosition;
              const beforeCursor = value.substring(0, cursorPos);
              const lines = beforeCursor.split('\n');
              const currentLine = lines[lines.length - 1];
              const lineNumber = lines.length - 1;
              
              // Show hint only on empty lines
              if (currentLine.trim() !== '') {
                return null;
              }
              
              // Calculate cursor position more accurately
              const computedStyle = window.getComputedStyle(descriptionRef);
              const lineHeight = parseFloat(computedStyle.lineHeight) || 24;
              const paddingTop = parseFloat(computedStyle.paddingTop) || 16;
              const paddingLeft = parseFloat(computedStyle.paddingLeft) || 16;
              
              // Position at cursor line with proper offset
              const lineY = paddingTop + (lineNumber * lineHeight) - (descriptionRef.scrollTop || 0) + 2;
              const lineX = paddingLeft + 2;
              
              return (
                <Box
                  sx={{
                    position: 'absolute',
                    top: `${lineY}px`,
                    left: `${lineX}px`,
                    pointerEvents: 'none',
                    color: alpha(theme.palette.text.secondary, 0.4),
                    fontSize: '0.9rem',
                    fontFamily: computedStyle.fontFamily,
                    lineHeight: `${lineHeight}px`,
                    zIndex: 1,
                    userSelect: 'none',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Type '/' for commands
                </Box>
              );
            })()}
          </Box>
          
          {/* Right Side - Live Preview */}
          {(value || previewMode) && (
            <Box sx={{ 
              width: previewMode ? '100%' : '50%', // Full width in preview mode
              height: '100%',
              borderLeft: previewMode ? 'none' : `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              overflow: 'auto',
              backgroundColor: alpha(theme.palette.grey[50], 0.3),
              p: 2,
              transition: 'width 0.3s ease, border-left 0.3s ease'
            }}>
              <Box sx={{
                '& ul': {
                  listStyle: 'none !important',
                  paddingLeft: '0 !important',
                  marginLeft: '0 !important'
                },
                '& ul li': {
                  listStyle: 'none !important',
                  listStyleType: 'none !important',
                  '&::marker': {
                    display: 'none !important'
                  },
                  '&::before': {
                    display: 'none !important'
                  }
                },
                '& ol': {
                  listStyle: 'decimal !important',
                  paddingLeft: '20px !important',
                  marginLeft: '0 !important'
                },
                '& ol li': {
                  listStyle: 'decimal !important',
                  listStyleType: 'decimal !important',
                  display: 'list-item !important',
                  '&::marker': {
                    display: 'block !important'
                  }
                }
              }}>
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => (
                      <h1 style={{
                        fontSize: '1.5rem',
                        fontWeight: 500,
                        margin: '24px 0 16px 0',
                        color: theme.palette.text.primary,
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        paddingBottom: '8px'
                      }}>
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 style={{
                        fontSize: '1.25rem',
                        fontWeight: 500,
                        margin: '20px 0 12px 0',
                        color: theme.palette.text.primary,
                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                        paddingBottom: '6px'
                      }}>
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 style={{
                        fontSize: '1.1rem',
                        fontWeight: 500,
                        margin: '16px 0 8px 0',
                        color: theme.palette.text.primary
                      }}>
                        {children}
                      </h3>
                    ),
                    p: ({ children }) => (
                      <p style={{
                        margin: '8px 0',
                        lineHeight: '1.6',
                        color: theme.palette.text.primary
                      }}>
                        {children}
                      </p>
                    ),
                    ul: ({ children, ...props }) => {
                      return (
                        <ul style={{
                          margin: '8px 0',
                          paddingLeft: '0px',
                          color: theme.palette.text.primary,
                          listStyle: 'none'
                        }}>
                          {children}
                        </ul>
                      );
                    },
                    ol: ({ children }) => (
                      <ol style={{
                        margin: '8px 0',
                        paddingLeft: '20px',
                        color: theme.palette.text.primary,
                        listStyle: 'decimal',
                        listStyleType: 'decimal'
                      }}>
                        {children}
                      </ol>
                    ),
                    li: ({ children, ...props }) => {
                      const childrenArray = React.Children.toArray(children);
                      const firstChild = childrenArray[0];
                      
                      // Check if this is a checkbox item
                      if (typeof firstChild === 'string') {
                        if (firstChild.startsWith('☐ ')) {
                          const taskText = firstChild.substring(2);
                          const handleCheckboxChange = () => {
                            const currentText = value;
                            const newText = currentText.replace(`- [ ] ${taskText}`, `- [x] ${taskText}`);
                            onChange(newText);
                          };
                          
                          return (
                            <li style={{
                              margin: '4px 0',
                              lineHeight: '1.5',
                              color: theme.palette.text.primary,
                              listStyleType: 'none',
                              display: 'flex',
                              alignItems: 'flex-start'
                            }}>
                              <Box
                                onClick={handleCheckboxChange}
                                sx={{
                                  width: '18px',
                                  height: '18px',
                                  border: '2px solid',
                                  borderColor: theme.palette.grey[400],
                                  borderRadius: '4px',
                                  backgroundColor: 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginRight: '8px',
                                  marginTop: '2px',
                                  '&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              />
                              <span>{taskText}{childrenArray.slice(1)}</span>
                            </li>
                          );
                        }
                        if (firstChild.startsWith('☑ ')) {
                          const taskText = firstChild.substring(2);
                          const handleCheckboxChange = () => {
                            const currentText = value;
                            const newText = currentText.replace(`- [x] ${taskText}`, `- [ ] ${taskText}`);
                            onChange(newText);
                          };
                          
                          return (
                            <li style={{
                              margin: '4px 0',
                              lineHeight: '1.5',
                              color: theme.palette.text.primary,
                              listStyleType: 'none',
                              display: 'flex',
                              alignItems: 'flex-start',
                              textDecoration: 'line-through',
                              opacity: 0.7
                            }}>
                              <Box
                                onClick={handleCheckboxChange}
                                sx={{
                                  width: '18px',
                                  height: '18px',
                                  border: '2px solid',
                                  borderColor: theme.palette.success.main,
                                  borderRadius: '4px',
                                  backgroundColor: theme.palette.success.main,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginRight: '8px',
                                  marginTop: '2px',
                                  '&:hover': {
                                    backgroundColor: theme.palette.success.dark,
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <Typography sx={{ 
                                  color: 'white', 
                                  fontSize: '14px', 
                                  lineHeight: 1,
                                  fontWeight: 700
                                }}>
                                  ✓
                                </Typography>
                              </Box>
                              <span>{taskText}{childrenArray.slice(1)}</span>
                            </li>
                          );
                        }
                      }
                      
                      // Check if parent is OL for numbered list
                      const parent = props.node?.parent;
                      const isNumberedList = parent && parent.tagName === 'ol';
                      
                      if (isNumberedList) {
                        // For numbered lists, use default browser styling
                        return (
                          <li style={{
                            margin: '4px 0',
                            lineHeight: '1.5',
                            color: theme.palette.text.primary,
                            listStyleType: 'decimal',
                            listStyle: 'decimal',
                            display: 'list-item'
                          }}>
                            {children}
                          </li>
                        );
                      }
                      
                      // Regular bullet list item
                      return (
                        <li style={{
                          margin: '4px 0',
                          lineHeight: '1.5',
                          color: theme.palette.text.primary,
                          display: 'flex',
                          alignItems: 'flex-start'
                        }}>
                          <span style={{
                            marginRight: '8px',
                            marginTop: '1px',
                            color: theme.palette.text.primary
                          }}>•</span>
                          <span>{children}</span>
                        </li>
                      );
                    },
                  blockquote: ({ children }) => (
                    <blockquote style={{
                      margin: '16px 0',
                      padding: '12px 16px',
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                      backgroundColor: alpha(theme.palette.grey[100], 0.5),
                      borderRadius: '0 4px 4px 0',
                      fontStyle: 'italic',
                      color: theme.palette.text.secondary
                    }}>
                      {children}
                    </blockquote>
                  ),
                  table: ({ children }) => (
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      margin: '16px 0',
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`
                    }}>
                      {children}
                    </table>
                  ),
                  thead: ({ children }) => (
                    <thead style={{
                      backgroundColor: alpha(theme.palette.grey[100], 0.8)
                    }}>
                      {children}
                    </thead>
                  ),
                  th: ({ children }) => (
                    <th style={{
                      padding: '12px',
                      textAlign: 'left',
                      fontWeight: 500,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                      backgroundColor: alpha(theme.palette.grey[100], 0.8),
                      color: theme.palette.text.primary
                    }}>
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td style={{
                      padding: '12px',
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                      color: theme.palette.text.primary
                    }}>
                      {children}
                    </td>
                  ),
                  hr: () => (
                    <hr style={{
                      margin: '24px 0',
                      border: 'none',
                      height: '2px',
                      backgroundColor: alpha(theme.palette.grey[400], 0.3),
                      borderRadius: '1px'
                    }} />
                  ),
                              strong: ({ children }) => (
                                <strong style={{
                                  fontWeight: 700,
                                  color: theme.palette.text.primary
                                }}>
                                  {children}
                                </strong>
                              ),
                  em: ({ children }) => (
                    <em style={{
                      fontStyle: 'italic',
                      color: theme.palette.text.secondary
                    }}>
                      {children}
                    </em>
                  ),
                  code: ({ inline, className, children, ...props }) => {
                    if (inline) {
                      return (
                        <code
                          style={{
                            backgroundColor: alpha(theme.palette.grey[100], 0.8),
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                            fontSize: '0.85em',
                            color: theme.palette.text.primary
                          }}
                          {...props}
                        >
                          {children}
                        </code>
                      );
                    }
                    return (
                      <pre
                        style={{
                          backgroundColor: alpha(theme.palette.grey[900], 0.05),
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                          borderRadius: '8px',
                          padding: '16px',
                          margin: '16px 0',
                          overflow: 'auto',
                          fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                          fontSize: '0.9em',
                          lineHeight: '1.6',
                          color: theme.palette.text.primary,
                          whiteSpace: 'pre',
                          tabSize: 4
                        }}
                      >
                        <code {...props}>{children}</code>
                      </pre>
                    );
                    },
                    blockquote: ({ children }) => (
                      <blockquote style={{
                        margin: '16px 0',
                        padding: '12px 16px',
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                        backgroundColor: alpha(theme.palette.grey[100], 0.5),
                        borderRadius: '0 4px 4px 0',
                        fontStyle: 'italic',
                        color: theme.palette.text.secondary
                      }}>
                        {children}
                      </blockquote>
                    ),
                    table: ({ children }) => (
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        margin: '16px 0',
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`
                      }}>
                        {children}
                      </table>
                    ),
                    thead: ({ children }) => (
                      <thead style={{
                        backgroundColor: alpha(theme.palette.grey[100], 0.8)
                      }}>
                        {children}
                      </thead>
                    ),
                    th: ({ children }) => (
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: 500,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                        backgroundColor: alpha(theme.palette.grey[100], 0.8),
                        color: theme.palette.text.primary
                      }}>
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td style={{
                        padding: '12px',
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                        color: theme.palette.text.primary
                      }}>
                        {children}
                      </td>
                    ),
                    hr: () => (
                      <hr style={{
                        margin: '24px 0',
                        border: 'none',
                        height: '2px',
                        backgroundColor: alpha(theme.palette.grey[400], 0.3),
                        borderRadius: '1px'
                      }} />
                    ),
                              strong: ({ children }) => (
                                <strong style={{
                                  fontWeight: 700,
                                  color: theme.palette.text.primary
                                }}>
                                  {children}
                                </strong>
                              ),
                    em: ({ children }) => (
                      <em style={{
                        fontStyle: 'italic',
                        color: theme.palette.text.secondary
                      }}>
                        {children}
                      </em>
                    ),
                    code: ({ inline, className, children, ...props }) => {
                      if (inline) {
                        return (
                          <code
                            style={{
                              backgroundColor: alpha(theme.palette.grey[100], 0.8),
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                              fontSize: '0.85em',
                              color: theme.palette.text.primary
                            }}
                            {...props}
                          >
                            {children}
                          </code>
                        );
                      }
                      return (
                        <pre
                          style={{
                            backgroundColor: alpha(theme.palette.grey[900], 0.05),
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                            borderRadius: '8px',
                            padding: '16px',
                            margin: '16px 0',
                            overflow: 'auto',
                            fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                            fontSize: '0.9em',
                            lineHeight: '1.6',
                            color: theme.palette.text.primary,
                            whiteSpace: 'pre',
                            tabSize: 4
                          }}
                        >
                          <code {...props}>{children}</code>
                        </pre>
                      );
                    }
                  }}
                >
                  {(() => {
                    let content = value || '*Type to see live preview...*';
                    // Pre-process to fix checkbox syntax
                    content = content.replace(/- \[ \]/g, '- ☐');
                    content = content.replace(/- \[x\]/g, '- ☑');
                    return content;
                  })()}
                </ReactMarkdown>
              </Box>
            </Box>
          )}
        </Box>
          
        {/* Command Menu */}
        {commandMenuOpen && (
          <Popper
            open={commandMenuOpen}
            anchorEl={commandMenuAnchor}
            placement="bottom-start"
            sx={{ zIndex: 9999 }}
          >
            <ClickAwayListener onClickAway={() => setCommandMenuOpen(false)}>
              <Card sx={{ 
                mt: 1, 
                maxWidth: 300,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`
              }}>
                <Box sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
                  {(() => {
                    // Use the same logic as handleInputChange
                    const cursorPos = descriptionRef?.selectionStart || value.length;
                    const beforeCursor = value.substring(0, cursorPos);
                    const lines = beforeCursor.split('\n');
                    const currentLine = lines[lines.length - 1];
                    const searchTerm = currentLine.startsWith('/') ? currentLine.substring(1).toLowerCase() : '';
                    let currentIndex = 0;
                    
                    return commandSections.map((section) => {
                      const sectionCommands = section.commands.filter(cmd => {
                        if (searchTerm === '') return true;
                        
                        const nameMatch = cmd.name.substring(1).toLowerCase().includes(searchTerm);
                        const aliasMatch = cmd.alias.substring(1).toLowerCase().includes(searchTerm);
                        const descMatch = cmd.description.toLowerCase().includes(searchTerm);
                        
                        // Special keyword matching
                        const keywordMatches = {
                          'head': cmd.name.includes('heading'),
                          'header': cmd.name.includes('heading'),
                          'h': cmd.name.includes('heading'),
                          'list': cmd.name.includes('bullet') || cmd.name.includes('number') || cmd.name.includes('checklist'),
                          'table': cmd.name.includes('table') || cmd.name.includes('column'),
                          'banner': cmd.name.includes('info') || cmd.name.includes('success') || cmd.name.includes('warning') || cmd.name.includes('danger'),
                          'format': cmd.name.includes('bold') || cmd.name.includes('italic') || cmd.name.includes('code'),
                          'text': cmd.name.includes('text') || cmd.name.includes('quote') || cmd.name.includes('bold') || cmd.name.includes('italic')
                        };
                        
                        const keywordMatch = keywordMatches[searchTerm] || false;
                        
                        return nameMatch || aliasMatch || descMatch || keywordMatch;
                      });
                      
                      if (sectionCommands.length === 0) return null;
                      
                      const sectionItems = [];
                      
                      // Section header
                      sectionItems.push(
                        <Box key={section.title} sx={{ 
                          px: 2, 
                          py: 1, 
                          backgroundColor: alpha(theme.palette.grey[100], 0.5),
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                        }}>
                          <Typography variant="caption" sx={{ 
                            fontWeight: 600, 
                            color: theme.palette.primary.main,
                            fontSize: '0.75rem',
                            letterSpacing: '0.5px'
                          }}>
                            {section.title}
                          </Typography>
                        </Box>
                      );
                      
                      // Section commands
                      sectionCommands.forEach((command) => {
                        const isSelected = currentIndex === selectedCommandIndex;
                        sectionItems.push(
                          <ListItem
                            key={command.name}
                            button
                            selected={isSelected}
                            onClick={() => executeCommand(command)}
                            sx={{
                              py: 1.5,
                              px: 2,
                              backgroundColor: isSelected 
                                ? alpha(theme.palette.primary.main, 0.1) 
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.05)
                              }
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                color: theme.palette.text.secondary
                              }}>
                                {command.icon}
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                  {command.description.split(' ')[0]} {command.description.split(' ')[1] || ''}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                  {command.description}
                                </Typography>
                              }
                            />
                          </ListItem>
                        );
                        currentIndex++;
                      });
                      
                      return sectionItems;
                    }).filter(Boolean).flat();
                  })()}
                </Box>
              </Card>
            </ClickAwayListener>
          </Popper>
        )}
      </Box>
    </Box>
  );
};

export default JobDescriptionMarkdown;